// src/components/PurchaseOrder/Footer/index.jsx
import React, { useMemo, useState } from 'react'
import { normalizePurchaseOrderCharges, purchaseOrderChargeIdentity } from '../../../../utils/purchaseOrderCharges'
import {
  Box, Button, Grid, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, Chip, Link
} from '@mui/material'
import styles from './index.module.css'

const TEXT_GREY = '#55656d'
const toNum = (v) => Number.parseFloat(v) || 0
const normalizeBool = (v) =>
  v === true || v === 1 || v === '1' || String(v).toLowerCase?.() === 'true'

// Elite Awards identifiers (scoped override only)
const ELITE_SUPPLIER_ID = '2147483647'
const ELITE_DECORATOR_ID = '4'

const isEliteInHouse = (po) => {
  const sid = String(po?.supplierInfo?.supplierId ?? '')
  const did = String(po?.decoratorId ?? '')
  return sid === ELITE_SUPPLIER_ID || sid === ELITE_DECORATOR_ID || did === ELITE_DECORATOR_ID
}

// Try to read a tax rate from the PO; fall back to 5%
const extractTaxRate = (po) => {
  const t = po?.tax || po?.defaultTax || po?.salesOrder?.tax
  if (t == null) return 0.05 // fallback 5%
  if (typeof t === 'number') return t > 1 ? t / 100 : t
  if (typeof t === 'object') {
    const n = t.rate ?? t.taxRate ?? t.percentage ?? t.value
    if (typeof n === 'number') return n > 1 ? n / 100 : n
  }
  return 0.05 // fallback 5%
}

const unitCostPO = (c) =>
  toNum(
    c?.NetCost ??
    c?.netCost ??
    c?.unitCost ??
    c?.UnitCost ??
    c?.Cost ??
    c?.SupplierCost ??
    0
  )

// prefer NetCost, with safe fallbacks
const unitNet = (c) =>
  toNum(c?.NetCost ?? c?.netCost ?? c?.unitCost ?? c?.UnitCost ?? c?.RetailPrice ?? c?.Retail ?? 0)

// pick explicit total if provided (net first), else compute unit*qty
const totalNet = (c) => {
  const explicitNet = c?.totalNet ?? c?.TotalNet
  const explicitNum = explicitNet != null ? toNum(explicitNet) : NaN
  if (Number.isFinite(explicitNum) && explicitNum > 0) return explicitNum
  const unit = unitCostPO(c)
  const qty  = toNum(c?.quantity ?? c?.Quantity ?? 1)
  return unit * qty
}

// build a stable key so identical service/charge lines are only counted once
const chargeKey = (c) => {
  return purchaseOrderChargeIdentity(c)
}

// Array normalizer for Charge column (can be JSON or array)
const chargesArray = (raw) => {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string') {
    try { const a = JSON.parse(raw); return Array.isArray(a) ? a : [] } catch { return [] }
  }
  return []
}

// Gather all decoration rows present anywhere on the PO
const collectAllDecorations = (po, items) => {
  const fromItems = (items || []).flatMap(i => Array.isArray(i?.decorations) ? i.decorations : [])
  const fromTop   = Array.isArray(po?.decorations) ? po.decorations : []
  // De-dupe by a simple structural signature (OrderItemId+methodName+decoratorId if present)
  const seen = new Set()
  const all  = []
  for (const d of [...fromTop, ...fromItems]) {
    const det = d?.DecoratorDetails || d?.decoratorDetails || {}
    const sig = [
      String(d?.OrderItemId ?? d?.orderItemId ?? ''),
      (det?.methodName ?? '').trim().toLowerCase(),
      String(det?.decoratorId ?? '')
    ].join('|')
    if (!seen.has(sig)) { seen.add(sig); all.push(d) }
  }
  return all
}

// Header-level charges (po.charges || po.supplierInfo.charges)
const collectHeaderCharges = (po) => {
  if (Array.isArray(po?.charges)) return normalizePurchaseOrderCharges(po.charges)
  if (Array.isArray(po?.supplierInfo?.charges)) return normalizePurchaseOrderCharges(po.supplierInfo.charges)
  return []
}

const formatAddressLines = (address) => {
  if (!address) return []
  const line1 = address.addressLine1 || ''
  const line2 = address.addressLine2 || ''
  const city = address.city || ''
  const state = address.stateProvince || ''
  const postal = address.postalCode || ''
  const country = address.country || ''
  const cityLine = [city, state].filter(Boolean).join(', ')
  const postalLine = [postal, country].filter(Boolean).join(' ')
  return [line1, line2, cityLine, postalLine].filter(Boolean)
}

const Footer = ({ purchaseOrderDetails, handleApprove, onRequestChange, busy = false, footerAddress, termsHtml }) => {
  const po = purchaseOrderDetails || {}
  const {
    items = [],
    currency = '',
    purchaseOrderType,
  } = po

  // statuses (robust)
  const supplierStatus = String(po?.supplierStatus || '').toLowerCase()
  const prodRepStatus  = String(po?.productionRepStatus || '').toLowerCase()
  const changeRequested = supplierStatus === 'problem' || prodRepStatus === 'problem'
  const approved        = supplierStatus === 'approved' || normalizeBool(po?.orderApproved)
  const lockedByProblem = prodRepStatus === 'problem'
  
  // ── Request Change dialog state ─────────────────────────────
  const [open, setOpen] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')

  const emailValid = !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const canSubmitChange =
    !busy && firstName.trim() && lastName.trim() && emailValid && message.trim()

  const submitRequestChange = async () => {
    if (!canSubmitChange) return
    const details = [
      message.trim(),
      firstName && lastName ? `Requested by: ${firstName} ${lastName}` : '',
      email ? `Email: ${email}` : ''
    ].filter(Boolean).join(' — ')
  
    await onRequestChange?.({
      requestChange: true,
      message: details,
    })
    setOpen(false)
  }

  // ── service-only charges (Other_service_art) with de-duplication ───────────
  const { serviceCharges, serviceTotal } = useMemo(() => {
    if (purchaseOrderType !== 'Other_service_art') {
      return { serviceCharges: [], serviceTotal: 0 }
    }

    const fromItems = (items || []).flatMap(i => Array.isArray(i?.charges) ? i.charges : [])
    // Prefer top-level charges; fall back to supplierInfo
    const headerCharges = collectHeaderCharges(po)

    // Combine then de-dupe
    const combined = [...fromItems, ...headerCharges]
    const map = new Map()
    for (const c of combined) {
      const k = chargeKey(c)
      if (!map.has(k)) map.set(k, c)
    }
    const unique = Array.from(map.values())

    // 👉 Net-based totals
    const total = unique.reduce((sum, c) => sum + totalNet(c), 0)

    return { serviceCharges: unique, serviceTotal: total }
  }, [items, po, purchaseOrderType])

  // ── standard totals (numeric-safe) — now NetCost-based ─────────────────
  const { productSubtotal, totalUnits, decorationSubtotal, totalImprinting } = useMemo(() => {
    if (purchaseOrderType === 'Other_service_art') {
      return { productSubtotal: 0, totalUnits: 0, decorationSubtotal: 0, totalImprinting: 0 }
    }

    // Product subtotal = sum(NetCost * Quantity)
    const productSubtotal = items.reduce((sum, item) => {
      const itemTotal = (item?.pricings || []).reduce((acc, p) => {
        const unit = toNum(p?.NetCost ?? p?.netCost)
        const qty  = toNum(p?.Quantity)
        return acc + (unit * qty)
      }, 0)
      return sum + itemTotal
    }, 0)

    const totalUnits = items.reduce((sum, item) => {
      const qty = (item?.pricings || []).reduce((acc, p) => acc + toNum(p?.Quantity), 0)
      return sum + qty
    }, 0)

    // === Decoration charges subtotal ======================================
    // 1) Start with all decorations found (top-level + per-item)
    const allDecos = collectAllDecorations(po, items)

    // 2) Sum charges from each decoration row
    const decoChargeMap = new Map()
    for (const deco of allDecos) {
      for (const ch of chargesArray(deco?.Charge)) {
        const k = chargeKey(ch)
        if (!decoChargeMap.has(k)) decoChargeMap.set(k, ch)
      }
    }

    // 3) Elite-only bridge: include header charges too (de-duped)
    if (isEliteInHouse(po)) {
      for (const ch of collectHeaderCharges(po)) {
        const k = chargeKey(ch)
        if (!decoChargeMap.has(k)) decoChargeMap.set(k, ch)
      }
    }

    const decorationSubtotal = Array.from(decoChargeMap.values())
      .reduce((sum, ch) => sum + totalNet(ch), 0)

    // 4) Imprint total (kept as-is)
    const totalImprinting = items.reduce((sum, item) => {
      const qty = (item.pricings || []).reduce((acc, p) => acc + toNum(p?.Quantity), 0)
      const imprint = (item.ArtWorks || []).reduce((acc, art) => acc + (toNum(art?.Charge) * qty), 0)
      return sum + imprint
    }, 0)

    return { productSubtotal, totalUnits, decorationSubtotal, totalImprinting }
  }, [items, po, purchaseOrderType])

  const taxRate = extractTaxRate(po)

  // rows by PO type
  let rows = []
  switch (purchaseOrderType) {
    case 'Other_service_art': {
      const sub = serviceTotal
      const tax = sub * taxRate
      const tot = sub + tax
      rows = [
        ['Service Lines', serviceCharges.length],
        ['Subtotal', sub],
        [`Tax (${(taxRate * 100).toFixed(2)}%)`, tax],
        ['Order Total', `${currency} ${tot.toFixed(2)}`],
      ]
      break
    }
    case 'Different_supplier_decoration': {
      // ✅ Elite in-house exception: also include product unit costs
      const includeProduct = isEliteInHouse(po)
      const sub = (includeProduct ? productSubtotal : 0) + decorationSubtotal + totalImprinting
      const tax = sub * taxRate
      const tot = sub + tax

      rows = [
        ['Total Units', totalUnits],
        ...(includeProduct ? [['Total Unit Costs', productSubtotal]] : []),
        ['Setup Charges', decorationSubtotal],
        ['Imprint Charges', totalImprinting],
        ['Subtotal', sub],
        [`Tax (${(taxRate * 100).toFixed(2)}%)`, tax],
        ['Order Total', `${currency} ${tot.toFixed(2)}`],
      ]
      break
    }
    case 'Same_supplier_decoration': {
      const sub = productSubtotal + decorationSubtotal
      const tax = sub * taxRate
      const tot = sub + tax
      rows = [
        ['Total Units Ordered', totalUnits],
        ['Total Unit Costs', productSubtotal],
        ['Total Setup Charge', decorationSubtotal],
        ['Subtotal', sub],
        [`Tax (${(taxRate * 100).toFixed(2)}%)`, tax],
        ['Order Total', `${currency} ${tot.toFixed(2)}`],
      ]
      break
    }
    case 'Combined_supplier_and_external_decoration': {
      const sub = productSubtotal + decorationSubtotal + totalImprinting
      const tax = sub * taxRate
      const tot = sub + tax
      rows = [
        ['Total Units Ordered', totalUnits],
        ['Total Unit Costs', productSubtotal],
        ['Total Setup Charge', decorationSubtotal],
        ['Total Imprint Cost', totalImprinting],
        ['Subtotal', sub],
        [`Tax (${(taxRate * 100).toFixed(2)}%)`, tax],
        ['Order Total', `${currency} ${tot.toFixed(2)}`],
      ]
      break
    }
    case 'No_decoration':
    default: {
      const sub = productSubtotal
      const tax = sub * taxRate
      const tot = sub + tax
      rows = [
        ['Total Units Ordered', totalUnits],
        ['Total Unit Costs', productSubtotal],
        ['Subtotal', sub],
        [`Tax (${(taxRate * 100).toFixed(2)}%)`, tax],
        ['Order Total', `${currency} ${tot.toFixed(2)}`],
      ]
      break
    }
  }

  // derived contacts
  const repFull = `${po?.productionRep?.first_name || ''} ${po?.productionRep?.last_name || ''}`.trim()
  const repRole = po?.productionRep?.role ? ` (${po.productionRep.role})` : ''
  const repEmail = po?.productionRep?.email
  const repPhone = po?.productionRep?.phone_number

  const supplierName  = po?.supplierInfo?.supplierName || ''
  const supplierEmail = po?.supplierInfo?.supplierEmail || ''
  const poNumber      = po?.poNumber

  const companyName =
    footerAddress?.companyName || 'Elite Promotional Marketing (Elite Sportswear & Awards Ltd)'
  const addressLines = formatAddressLines(footerAddress)

  return (
    <>
      <Grid container className={styles.footerContainer}>
        {/* Left: contact block (with missing fields) */}
        <Grid item md={6} xs={12}>
          <Typography variant="h6">Questions about this purchase order?</Typography>
          {
          //JSON.stringify(purchaseOrderDetails)
          }
          <Box sx={{ mt: 0.5 }}>
            <Typography color={TEXT_GREY} variant='body2' sx={{ fontWeight: 600 }}>
              {repFull || 'Purchasing Rep'}
              {repRole}
            </Typography>
            <Typography color={TEXT_GREY} variant='body2'>
              {repEmail ? <Link href={`mailto:${repEmail}`}>{repEmail}</Link> : 'N/A'}
            </Typography>
            <Typography color={TEXT_GREY} variant='body2'>
              {repPhone ? <Link href={`tel:${repPhone}`}>{repPhone}</Link> : 'N/A'}
            </Typography>
          </Box>

          {/* Company block */}
          <Box sx={{ mt: 2 }}>
            <Typography color={TEXT_GREY} variant="body2" sx={{ fontWeight: 600 }}>
              {companyName}
            </Typography>
            {addressLines.length ? (
              addressLines.map((line) => (
                <Typography key={line} color={TEXT_GREY} variant="body2">
                  {line}
                </Typography>
              ))
            ) : (
              <>
                <Typography color={TEXT_GREY} variant="body2">14703-118 Ave NW</Typography>
                <Typography color={TEXT_GREY} variant="body2">Edmonton, Alberta T5L 2M7</Typography>
                <Typography color={TEXT_GREY} variant="body2">Canada</Typography>
              </>
            )}
          </Box>
        </Grid>

        {/* Right: totals */}
        <Grid item md={6} xs={12} className={styles.totalPriceContainer}>
          {rows.map(([label, val], i) => (
            <Box key={i} className={styles.titleValue}>
              <Typography variant="body1" className={styles.title} gutterBottom>
                {label}
              </Typography>
              <Typography variant="body1" className={styles.value}>
                {(() => {
                  if (/unit/i.test(label)) return val
                  if (label === 'Service Lines') return val
                  if (label === 'Order Total' && typeof val === 'string') {
                    const [curr, amt] = val.split(' ')
                    return `${curr} $${amt}`
                  }
                  if (typeof val === 'number') return `$${val.toFixed(2)}`
                  return val
                })()}
              </Typography>
            </Box>
          ))}
        </Grid>

        {/* Status + actions */}
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mt: 2, gap: 1 }}>
          {changeRequested && (
            <Alert
              severity="error"
              variant="filled"
              sx={{
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.5,
                '& .MuiAlert-message': { padding: 0, margin: 0, minWidth: 'unset', display: 'inline' },
              }}
            >
              {po?.proofComments
                ? `Changes requested — ${po.proofComments}`
                : 'Changes requested — awaiting review'}
            </Alert>
          )}
          {approved && !changeRequested && (
            <Chip color="success" label="This PO has been approved by the supplier" sx={{ alignSelf: 'flex-start', fontWeight: 600 }} />
          )}

          <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              className={styles.AddCommentButton}
              onClick={() => handleApprove({ orderApproved: true })}
              disabled={approved || lockedByProblem || busy}
              sx={{
                '&.Mui-disabled': {
                  color: 'white !important',
                  backgroundColor: 'gray !important',
                  opacity: 0.4,
                  cursor: 'not-allowed',
                  pointerEvents: 'none',
                },
              }}
            >
              <Typography className={styles.ButtonTypography}>
                {approved ? 'Order Approved' : 'Approve Order'}
              </Typography>
            </Button>

            <Button
              className={`${styles.AddCommentButton} ${styles.noButton}`}
              onClick={() => setOpen(true)}
              disabled={approved || lockedByProblem || busy || changeRequested}
              sx={{
                '&.Mui-disabled': {
                  color: 'white !important',
                  backgroundColor: '#888 !important',
                  opacity: 0.4,
                  cursor: 'not-allowed',
                  pointerEvents: 'none',
                },
              }}
            >
              <Typography className={styles.ButtonTypography}>Request Change</Typography>
            </Button>
          </Box>

          {lockedByProblem && (
            <Typography variant="body2" sx={{ color: '#b71c1c', mt: 0.5 }}>
              Actions are locked while status is <strong>Problem</strong>. A production rep must set the status back to <strong>OK</strong>.
            </Typography>
          )}
        </Box>
      </Grid>
      {termsHtml?.trim() ? (
        <Grid className={styles.termsContainer}>
          <Box dangerouslySetInnerHTML={{ __html: termsHtml }} />
        </Grid>
      ) : (
        <Grid className={styles.termsContainer}>
          <Box>
            <Typography className={styles.Titleheading}>Terms and Conditions</Typography>
          </Box>
          <Box className={styles.headingValueContainer}>
            <Typography className={styles.headingValue}>
              Please review this purchase order carefully and contact us if any changes are required.
            </Typography>
          </Box>
        </Grid>
      )}
      {/* Request Change Dialog */}
      <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Changes</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid item xs={12} sm={6}>
              <TextField label="First name" fullWidth value={firstName} onChange={e => setFirstName(e.target.value)} disabled={busy} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Last name" fullWidth value={lastName} onChange={e => setLastName(e.target.value)} disabled={busy} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={email}
                onChange={e => setEmail(e.target.value)}
                error={!!email && !emailValid}
                helperText={!!email && !emailValid ? 'Enter a valid email' : ' '}
                disabled={busy}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Message"
                placeholder="Describe the change you need…"
                fullWidth
                multiline
                minRows={4}
                value={message}
                onChange={e => setMessage(e.target.value)}
                disabled={busy}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={submitRequestChange} disabled={!canSubmitChange}>
            {busy ? 'Sending…' : 'Send Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Footer
